import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  LogIn,
  AlertCircle,
  Info,
  Eye,
  EyeOff,
  Mail,
  Lock,
  Megaphone,
  ArrowRight,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { useTheme } from '../context/ThemeContext';
import { resolveAssetUrl } from '../services/api';

/**
 * Tela de login (Fase 28/30) — split-screen estilo Fortics/Stripe:
 *   Esquerda: formulário limpo com logo + nome do cliente + tagline
 *   Direita:  carrossel de imagens LIMPO (sem overlay/texto — a foto
 *             aparece nítida). Com múltiplas imagens, troca sozinho.
 *             Sem imagem, mostra o painel de anúncio configurável
 *             (Config LOGIN_BANNER_*) ou só o gradiente padrão.
 */
const CAROUSEL_INTERVAL_MS = 6000;
export default function LoginPage() {
  const { user, login, loading } = useAuth();
  const { clientName, logoSrc, login: loginCfg } = useBranding();
  const { resolved } = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slide, setSlide] = useState(0);

  // Carrossel — avança sozinho quando há mais de uma imagem
  const images = (loginCfg?.images ?? [])
    .map((u) => resolveAssetUrl(u))
    .filter((u): u is string => !!u);
  useEffect(() => {
    if (images.length < 2) return;
    const t = setInterval(
      () => setSlide((s) => (s + 1) % images.length),
      CAROUSEL_INTERVAL_MS,
    );
    return () => clearInterval(t);
  }, [images.length]);

  if (loading) {
    return (
      <div className={`${resolved} flex h-screen items-center justify-center bg-canvas text-fg-2`}>
        Carregando…
      </div>
    );
  }
  if (user) return <Navigate to="/" replace />;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email || !password) {
      setError('Preencha email e senha');
      return;
    }
    setSubmitting(true);
    setError(null);
    const r = await login(email, password);
    setSubmitting(false);
    if (r.ok) {
      navigate('/', { replace: true });
    } else {
      setError(r.error ?? 'Falha no login');
    }
  }

  const hasBanner = !!(loginCfg?.title || loginCfg?.body);

  return (
    <div className={`${resolved} flex min-h-screen bg-canvas`}>
      {/* ── Coluna esquerda: formulário ─────────────────────────────── */}
      <div className="w-full lg:w-[440px] xl:w-[480px] flex-shrink-0 flex flex-col justify-center px-8 sm:px-12 py-10 bg-surface lg:border-r border-line">
        <div className="w-full max-w-sm mx-auto">
          {/* Logo */}
          <div className="mb-10">
            {logoSrc ? (
              <img
                src={logoSrc}
                alt={clientName}
                className="h-14 w-auto max-w-[11rem] object-contain mb-4"
              />
            ) : (
              <div className="inline-flex w-12 h-12 rounded-2xl bg-primary-solid items-center justify-center font-bold text-white text-xl mb-4">
                {clientName.charAt(0).toUpperCase()}
              </div>
            )}
            <h1 className="text-2xl font-bold text-fg">{clientName}</h1>
            <p className="text-sm text-muted mt-1">
              Gestão completa do seu provedor em um só lugar.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-fg-2 mb-1.5">
                E-mail de login
              </label>
              <div className="relative">
                <Mail
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted"
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  autoFocus
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-canvas border border-active text-fg placeholder-slate-600 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  placeholder="voce@empresa.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-fg-2 mb-1.5">Senha</label>
              <div className="relative">
                <Lock
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted"
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className="w-full pl-9 pr-11 py-2.5 rounded-xl bg-canvas border border-active text-fg placeholder-slate-600 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-fg-2"
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2 px-3 py-2 rounded-xl bg-danger-soft/30 border border-danger-line/50 text-sm text-danger">
                <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary-solid hover:bg-primary-solid-hover disabled:bg-primary-soft disabled:cursor-not-allowed text-white font-semibold transition"
            >
              {submitting ? (
                'Entrando…'
              ) : (
                <>
                  <LogIn size={18} />
                  Entrar
                </>
              )}
            </button>
          </form>

          {/* Aviso configurável (Config LOGIN_NOTICE) */}
          {loginCfg?.notice && (
            <div className="mt-8 rounded-xl bg-canvas border border-line p-4">
              <div className="flex items-start gap-2.5">
                <Info size={16} className="text-primary mt-0.5 flex-shrink-0" />
                <p className="text-xs leading-relaxed text-fg-2 whitespace-pre-line">
                  {loginCfg.notice}
                </p>
              </div>
            </div>
          )}

          <p className="text-center text-xs text-faint mt-8">
            Sem cadastro público — solicite ao administrador.
          </p>
        </div>
      </div>

      {/* ── Coluna direita: carrossel de imagens OU painel de anúncio ── */}
      {images.length > 0 ? (
        /* Área da foto LIMPA — sem overlay/glass/texto, imagem nítida */
        <div className="hidden lg:block flex-1 relative overflow-hidden bg-surface">
          {images.map((src, i) => (
            <img
              key={src}
              src={src}
              alt=""
              aria-hidden
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                i === slide ? 'opacity-100' : 'opacity-0'
              }`}
            />
          ))}
          {images.length > 1 && (
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((src, i) => (
                <button
                  key={src}
                  type="button"
                  onClick={() => setSlide(i)}
                  aria-label={`Imagem ${i + 1}`}
                  className={`h-2 rounded-full transition-all ${
                    i === slide ? 'w-6 bg-white' : 'w-2 bg-white/50 hover:bg-white/80'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="hidden lg:flex flex-1 relative overflow-hidden bg-gradient-to-br from-primary-solid-hover via-primary-solid to-info-solid items-center justify-center p-12">
          {/* Elementos decorativos */}
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-32 -left-16 w-[28rem] h-[28rem] rounded-full bg-cyan-300/20 blur-3xl" />
          <div className="absolute top-1/3 left-10 w-40 h-40 rounded-3xl bg-white/5 rotate-12" />
          <div className="absolute bottom-16 right-16 w-28 h-28 rounded-2xl bg-white/5 -rotate-6" />

          {hasBanner && (
            <div className="relative max-w-2xl text-center">
              {loginCfg?.badge && (
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border-2 border-white/60 bg-white/10 backdrop-blur text-white text-sm font-semibold mb-6">
                  <Megaphone size={15} />
                  {loginCfg.badge}
                </div>
              )}
              {loginCfg?.title && (
                <h2 className="text-4xl xl:text-5xl font-extrabold text-white leading-tight tracking-tight mb-5 drop-shadow-sm">
                  {loginCfg.title}
                </h2>
              )}
              {loginCfg?.body && (
                <p className="text-lg text-white/85 leading-relaxed whitespace-pre-line mb-8 max-w-xl mx-auto">
                  {loginCfg.body}
                </p>
              )}
              {loginCfg?.ctaText && (
                loginCfg.ctaUrl ? (
                  <a
                    href={loginCfg.ctaUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-white text-primary-solid font-bold shadow-lg hover:shadow-xl hover:scale-[1.02] transition"
                  >
                    {loginCfg.ctaText}
                    <ArrowRight size={17} />
                  </a>
                ) : (
                  <span className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-white text-primary-solid font-bold shadow-lg">
                    {loginCfg.ctaText}
                  </span>
                )
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
